// ─────────────────────────────────────────────────────────────────────────────
//  SortController
//  A Lit Reactive Controller that encapsulates all datatable sorting logic.
//
//  Responsibilities:
//    • Hold sort state: field, order, mode, multi-meta.
//    • Cycle through asc → desc (→ none when removableSort) on toggleSort().
//    • Produce a sorted copy of the data array via sortedData().
//    • Report per-column sort state (sorted?, order, priority rank) for headers.
//    • Fire a host-level 'uwc-sort' CustomEvent on every state change.
//
//  Usage inside UwcDatatable:
//
//    private _sort = new SortController(this);
//
//    // Presort: push initial values into the controller
//    override willUpdate(changed: PropertyValues) {
//      if (changed.has('sortField'))     this._sort.sortField     = this.sortField;
//      if (changed.has('sortOrder'))     this._sort.sortOrder     = this.sortOrder;
//      if (changed.has('sortMode'))      this._sort.sortMode      = this.sortMode;
//      if (changed.has('multiSortMeta')) this._sort.multiSortMeta = this.multiSortMeta ?? [];
//      if (changed.has('removableSort')) this._sort.removableSort = this.removableSort;
//      if (changed.has('customComparator')) this._sort.customComparator = this.customComparator;
//    }
//
//    // In render:
//    const sorted = this._sort.sortedData(rows);
//    // Per-column header:
//    const state  = this._sort.getColumnState(col.field);
//
// ─────────────────────────────────────────────────────────────────────────────
import type { ReactiveController, ReactiveControllerHost } from 'lit';

// ── Public types ─────────────────────────────────────────────────────────────

/** 1 = ascending, -1 = descending, 0 = unsorted. */
export type SortOrder = 1 | -1 | 0;

/** Single-column or multi-column sort mode. */
export type SortMode  = 'single' | 'multiple';

/** One column's sort entry used in multiSortMeta. */
export interface SortMeta {
  field: string;
  order: 1 | -1;
}

/** Detail payload of the `uwc-sort` event. */
export interface SortChangeDetail {
  sortField:     string | null;
  sortOrder:     SortOrder;
  sortMode:      SortMode;
  multiSortMeta: SortMeta[];
}

/** Per-column state consumed by the header cell renderer. */
export interface ColumnSortState {
  sorted: boolean;
  order:  SortOrder;
  /** 1-based priority rank in multiple-sort mode. Undefined in single mode. */
  rank?:  number;
}

// ─────────────────────────────────────────────────────────────────────────────
// SortController
// ─────────────────────────────────────────────────────────────────────────────

export class SortController implements ReactiveController {
  private readonly _host: ReactiveControllerHost & EventTarget;

  // ── Mutable sort state (single source of truth) ────────────────────────────

  sortField:    string | null = null;
  sortOrder:    SortOrder     = 1;
  sortMode:     SortMode      = 'single';
  multiSortMeta: SortMeta[]   = [];
  removableSort: boolean      = false;

  /**
   * Custom value comparator. When provided, replaces the built-in type-aware
   * comparison but is still composed with the sort machinery (order flip, multi-sort).
   *
   * (v1, v2, field, order) → negative | 0 | positive
   */
  customComparator?: (
    v1: unknown,
    v2: unknown,
    field: string,
    order: SortOrder,
  ) => number;

  constructor(host: ReactiveControllerHost & EventTarget) {
    this._host = host;
    host.addController(this);
  }

  hostConnected()    {}
  hostDisconnected() {}

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Toggle sort for the given column field.
   * Single mode:  none→asc→desc(→none if removableSort)
   * Multiple mode: add column asc, then desc, then remove (if removableSort).
   */
  toggleSort(field: string): void {
    if (this.sortMode === 'multiple') {
      this._toggleMulti(field);
    } else {
      this._toggleSingle(field);
    }
    this._host.requestUpdate();
    this._emit();
  }

  /**
   * Return a sorted copy of `data`.
   * Does not mutate the original array.
   */
  sortedData(data: Record<string, unknown>[]): Record<string, unknown>[] {
    if (this.sortMode === 'multiple') {
      if (!this.multiSortMeta.length) return data;
      return [...data].sort((a, b) => this._compareMulti(a, b));
    }
    if (!this.sortField || this.sortOrder === 0) return data;
    const field = this.sortField;
    const order = this.sortOrder;
    return [...data].sort((a, b) => this._compareSingle(a, b, field, order));
  }

  /**
   * Return the sort state for a given column field.
   * Used by the header cell renderer to display icons and aria-sort.
   */
  getColumnState(field: string): ColumnSortState {
    if (this.sortMode === 'multiple') {
      const idx = this.multiSortMeta.findIndex(m => m.field === field);
      if (idx === -1) return { sorted: false, order: 0 };
      return { sorted: true, order: this.multiSortMeta[idx].order, rank: idx + 1 };
    }
    if (this.sortField !== field) return { sorted: false, order: 0 };
    return { sorted: true, order: this.sortOrder };
  }

  // ── Toggle helpers ─────────────────────────────────────────────────────────

  private _toggleSingle(field: string): void {
    if (this.sortField === field) {
      if (this.sortOrder === 1) {
        this.sortOrder = -1;
      } else if (this.removableSort) {
        // 3rd click: clear sort entirely
        this.sortField = null;
        this.sortOrder = 1;
      } else {
        this.sortOrder = 1;
      }
    } else {
      this.sortField = field;
      this.sortOrder = 1;
    }
  }

  private _toggleMulti(field: string): void {
    const idx = this.multiSortMeta.findIndex(m => m.field === field);
    if (idx === -1) {
      // New column: add at end with ascending order
      this.multiSortMeta = [...this.multiSortMeta, { field, order: 1 }];
    } else {
      const current = this.multiSortMeta[idx];
      if (current.order === 1) {
        // asc → desc
        this.multiSortMeta = this.multiSortMeta.map((m, i) =>
          i === idx ? { ...m, order: -1 as const } : m,
        );
      } else if (this.removableSort) {
        // desc → remove
        this.multiSortMeta = this.multiSortMeta.filter((_, i) => i !== idx);
      } else {
        // desc → asc (cycle without removal)
        this.multiSortMeta = this.multiSortMeta.map((m, i) =>
          i === idx ? { ...m, order: 1 as const } : m,
        );
      }
    }
  }

  // ── Comparison helpers ─────────────────────────────────────────────────────

  private _compareSingle(
    a: Record<string, unknown>,
    b: Record<string, unknown>,
    field: string,
    order: SortOrder,
  ): number {
    const v1 = this._resolve(a, field);
    const v2 = this._resolve(b, field);
    if (this.customComparator) {
      return this.customComparator(v1, v2, field, order);
    }
    return this._compareValues(v1, v2) * (order as number);
  }

  private _compareMulti(
    a: Record<string, unknown>,
    b: Record<string, unknown>,
  ): number {
    for (const meta of this.multiSortMeta) {
      const result = this._compareSingle(a, b, meta.field, meta.order);
      if (result !== 0) return result;
    }
    return 0;
  }

  private _compareValues(v1: unknown, v2: unknown): number {
    if (v1 == null && v2 == null) return 0;
    if (v1 == null) return -1;
    if (v2 == null) return  1;
    if (typeof v1 === 'string' && typeof v2 === 'string') {
      return v1.localeCompare(v2, undefined, { numeric: true, sensitivity: 'base' });
    }
    if (typeof v1 === 'number' && typeof v2 === 'number') {
      return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
    }
    if (v1 instanceof Date && v2 instanceof Date) {
      return v1.getTime() - v2.getTime();
    }
    return String(v1).localeCompare(String(v2));
  }

  /** Traverse dot-notation paths: "address.city" → nested value. */
  private _resolve(row: Record<string, unknown>, field: string): unknown {
    return field.split('.').reduce<unknown>((acc, key) => {
      if (acc !== null && acc !== undefined && typeof acc === 'object') {
        return (acc as Record<string, unknown>)[key];
      }
      return undefined;
    }, row);
  }

  // ── Event ──────────────────────────────────────────────────────────────────

  private _emit(): void {
    const detail: SortChangeDetail = {
      sortField:     this.sortField,
      sortOrder:     this.sortOrder,
      sortMode:      this.sortMode,
      multiSortMeta: [...this.multiSortMeta],
    };
    this._host.dispatchEvent(
      new CustomEvent('uwc-sort', { bubbles: true, composed: true, detail }),
    );
  }
}
